import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCode,
	faCube,
	faDatabase,
	faGears,
	faToggleOff,
	faToggleOn,
} from '@fortawesome/pro-solid-svg-icons';
import {
	VARIABLE_TYPE_REGISTRY,
	type VariableTypeKey,
} from '@supply-mover-2-0/shared';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { Button } from '../../components/forms/Button';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import type { SelectSearchGroup } from '../../components/forms/SelectSearch';
import { SelectSearch } from '../../components/forms/SelectSearch';
import { TextArea } from '../../components/forms/TextArea';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

export interface AvailableDataField {
	key: string;
	label: string;
	type: string;
	example?: string;
	displayKey?: string; // Optional display version of the key
	objectType?: VariableTypeKey; // For object types, reference to type registry
	arrayType?: VariableTypeKey; // For array types, reference to type registry
	isExpandable?: boolean; // Whether this field can be expanded to show children
}

export interface AvailableDataSource {
	category: string;
	icon: unknown;
	fields: Array<AvailableDataField>;
}

interface DynamicFieldValueProps {
	label: string;
	value: string; // Always just a string value
	onChange: (value: string) => void; // Just pass back the string value
	dataType?: 'string' | 'number' | 'email' | 'textarea' | 'select';
	selectOptions?: Array<{ value: string; label: string }>;
	placeholder?: string;
	required?: boolean;
	availableData?: Array<AvailableDataSource>;
	description?: string;
	displayTransform?: (value: string) => string; // Optional function to transform value for display
	defaultMode?: 'static' | 'dynamic'; // Default mode when value is empty (defaults to 'dynamic')
	// Disables the value input in every branch AND the Static/Dynamic mode
	// toggle — used by VariableMapping's read-only mode.
	disabled?: boolean;
}

/**
 * Get nested fields for a type from the registry.
 * This enables expanding complex types to show their properties.
 */
function getNestedFields(
	baseKey: string,
	baseDisplayKey: string,
	typeKey: VariableTypeKey,
): Array<AvailableDataField> {
	const typeDef = VARIABLE_TYPE_REGISTRY[typeKey];

	if (typeDef == null) {
		return [];
	}

	const fields: Array<AvailableDataField> = [];

	for (const [propKey, propDef] of Object.entries(typeDef.properties)) {
		const fieldKey = `${baseKey}.${propKey}`;
		const displayLabel = propDef.label;
		const displayKeyPath = `${baseDisplayKey}.${displayLabel}`;

		const field: AvailableDataField = {
			key: fieldKey,
			label: displayLabel,
			type: propDef.type,
			displayKey: displayKeyPath,
		};

		// Mark as expandable if it has nested types
		if (propDef.objectType != null) {
			field.objectType = propDef.objectType;
			field.isExpandable = true;
		}

		if (propDef.arrayType != null) {
			field.arrayType = propDef.arrayType;
			field.isExpandable = true;
		}

		fields.push(field);
	}

	return fields;
}

export function DynamicFieldValue({
	label,
	value,
	onChange,
	dataType = 'string',
	selectOptions = [],
	placeholder,
	required = false,
	availableData = [],
	description,
	displayTransform,
	defaultMode = 'dynamic',
	disabled = false,
}: DynamicFieldValueProps): React.ReactNode {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Determine if value is dynamic based on the {{ }} pattern
	// When value is empty, use the defaultMode prop
	const isDynamic = value
		? value.startsWith('{{') && value.endsWith('}}')
		: defaultMode === 'dynamic';

	// Combine available data sources with defaults
	// Available data from props takes precedence
	const allDataSources: Array<AvailableDataSource> = [];

	// Add provided data sources first (these are the real available fields)
	if (availableData.length > 0) {
		allDataSources.push(...availableData);
	}

	// Only add system variables as fallback if no data sources provided
	if (allDataSources.length === 0) {
		// Only show system variables as defaults (no hard-coded entity data)
		allDataSources.push({
			category: t('System Variables'),
			icon: faGears,
			fields: [
				{
					key: 'system.currentDate',
					label: t('Current Date'),
					type: 'string',
					example: '2024-01-15',
				},
				{
					key: 'system.currentTime',
					label: t('Current Time'),
					type: 'string',
					example: '14:30:00',
				},
			],
		});
	} else {
		// Add system variables to provided data if not already included
		const hasSystemVars = allDataSources.some(
			(s) =>
				s.category === t('System Variables') ||
				s.category === t('Context Variables'),
		);

		if (!hasSystemVars) {
			allDataSources.push({
				category: t('System Variables'),
				icon: faGears,
				fields: [
					{
						key: 'system.currentDate',
						label: t('Current Date'),
						type: 'string',
						example: '2024-01-15',
					},
					{
						key: 'system.currentTime',
						label: t('Current Time'),
						type: 'string',
						example: '14:30:00',
					},
				],
			});
		}
	}

	// Local state for the dynamic mode toggle (controlled by the value pattern)
	const [isInDynamicMode, setIsInDynamicMode] = useState(isDynamic);

	// Sync mode state when value changes externally
	useEffect(() => {
		setIsInDynamicMode(isDynamic);
	}, [isDynamic]);

	// Build the options list with separate groups for top-level and nested fields
	const comboboxGroups = useMemo((): Array<SelectSearchGroup> => {
		const groups: Array<SelectSearchGroup> = [];

		for (const source of allDataSources) {
			if (source.fields.length === 0) {
				continue;
			}

			// Group 1: Top-level fields (always shown)
			const topLevelOptions = source.fields.map((field) => {
				const typeIcon = field.isExpandable
					? field.arrayType != null
						? '[] '
						: '{} '
					: '';

				return {
					value: `{{${field.key}}}`,
					label: `${typeIcon}${field.label}`,
					subtitle: field.displayKey ?? field.key,
					displayValue: field.displayKey
						? `{{${field.displayKey}}}`
						: `{{${field.key}}}`,
				};
			});

			groups.push({
				key: source.category,
				label: source.category,
				icon: source.icon as IconDefinition | undefined,
				options: topLevelOptions,
			});

			// Create separate property groups for each expandable top-level field
			// This keeps Order properties separate from Client properties, etc.
			for (const field of source.fields) {
				if (field.isExpandable !== true) {
					continue;
				}

				const nestedTypeKey = field.objectType ?? field.arrayType;

				if (nestedTypeKey == null) {
					continue;
				}

				const fieldNestedOptions: Array<{
					value: string;
					label: string;
					subtitle: string;
					displayValue: string;
				}> = [];

				// Get first level of nested fields
				const nestedFields = getNestedFields(
					field.key,
					field.displayKey ?? field.key,
					nestedTypeKey,
				);

				for (const nestedField of nestedFields) {
					const nestedTypeIcon = nestedField.isExpandable
						? nestedField.arrayType != null
							? '[] '
							: '{} '
						: '';

					fieldNestedOptions.push({
						value: `{{${nestedField.key}}}`,
						label: `${nestedTypeIcon}${nestedField.label}`,
						subtitle: nestedField.displayKey ?? nestedField.key,
						displayValue: nestedField.displayKey
							? `{{${nestedField.displayKey}}}`
							: `{{${nestedField.key}}}`,
					});

					// If this nested field is also expandable, add its children too (2nd level)
					if (nestedField.isExpandable === true) {
						const deepNestedTypeKey =
							nestedField.objectType ?? nestedField.arrayType;

						if (deepNestedTypeKey != null) {
							const deepNestedFields = getNestedFields(
								nestedField.key,
								nestedField.displayKey ?? nestedField.key,
								deepNestedTypeKey,
							);

							for (const deepField of deepNestedFields) {
								const deepTypeIcon = deepField.isExpandable
									? deepField.arrayType != null
										? '[] '
										: '{} '
									: '';

								fieldNestedOptions.push({
									value: `{{${deepField.key}}}`,
									label: `${nestedField.label} › ${deepTypeIcon}${deepField.label}`,
									subtitle: deepField.displayKey ?? deepField.key,
									displayValue: deepField.displayKey
										? `{{${deepField.displayKey}}}`
										: `{{${deepField.key}}}`,
								});
							}
						}
					}
				}

				// Create a separate group for this field's properties
				if (fieldNestedOptions.length > 0) {
					groups.push({
						key: `${source.category}-${field.key}-properties`,
						label: `${field.label} ${t('Properties')}`,
						icon: faCube,
						options: fieldNestedOptions,
					});
				}
			}
		}

		return groups.filter((group) => group.options.length > 0);
	}, [allDataSources, t]);

	const handleToggle = (): void => {
		setIsInDynamicMode(!isInDynamicMode);
		// Clear value when switching modes
		onChange('');
	};

	const handleStaticValueChange = (newValue: string): void => {
		onChange(newValue);
	};

	const renderStaticInput = (): React.ReactNode => {
		const inputProps = {
			value: !isInDynamicMode ? value : '',
			onChange: (
				e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
			) => {
				handleStaticValueChange(e.target.value);
			},
			placeholder: placeholder ?? t('Enter value'),
			required,
			disabled,
		};

		switch (dataType) {
			case 'textarea':
				return <TextArea {...inputProps} rows={3} />;
			case 'select':
				return (
					<Select
						options={selectOptions}
						value={!isInDynamicMode ? value : ''}
						onChange={handleStaticValueChange}
						placeholder={placeholder ?? t('Select value')}
						disabled={disabled}
					/>
				);
			case 'email':
				return <Input {...inputProps} type="email" />;
			case 'number':
				return <Input {...inputProps} type="number" />;
			default:
				return <Input {...inputProps} type="text" />;
		}
	};

	const renderDynamicInput = (): React.ReactNode => {
		const hasVariables =
			comboboxGroups.reduce((sum, group) => sum + group.options.length, 0) > 0;

		return (
			<div className="space-y-3">
				{/* Variable Selector */}
				<div>
					<SelectSearch
						groups={comboboxGroups}
						value={value ?? ''}
						onChange={(newValue: string) => {
							onChange(newValue);
						}}
						placeholder={
							hasVariables
								? t('Search for a variable...')
								: t('No variables available')
						}
						disabled={disabled || !hasVariables}
						emptyMessage={t('No matching variables found')}
						mode="static"
					/>

					{!hasVariables && (
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t(
								'No variables available from previous steps. Add steps to the flow to see available outputs.',
							)}
						</div>
					)}
				</div>

				{/* Selected value display with type info */}
				{value != null && value.length > 0 && (
					<div
						className={cn(
							'p-2 rounded-md text-xs font-mono',
							colors.classes.bgSecondary,
							colors.classes.textSecondary,
						)}
					>
						{displayTransform != null ? displayTransform(value) : value}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="space-y-3">
			{/* Label and Toggle */}
			<div className="flex items-center justify-between">
				<div>
					<label
						className={`text-sm font-medium ${colors.classes.textPrimary}`}
					>
						{label}{' '}
						{required && <span className={colors.classes.statusError}>*</span>}
					</label>
					{description != null && (
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{description}
						</div>
					)}
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleToggle}
					disabled={disabled}
					className="flex items-center gap-2"
					title={
						isInDynamicMode
							? t('Switch to hardcoded value')
							: t('Switch to dynamic value')
					}
				>
					<Icon
						icon={isInDynamicMode ? faToggleOn : faToggleOff}
						className={
							isInDynamicMode
								? colors.classes.statusSuccess
								: colors.classes.textSecondary
						}
					/>
					<span className="text-xs">
						{isInDynamicMode ? t('Dynamic') : t('Static')}
					</span>
				</Button>
			</div>

			{/* Input Field */}
			<div>{isInDynamicMode ? renderDynamicInput() : renderStaticInput()}</div>

			{/* Mode Indicator */}
			<div
				className={`text-xs ${colors.classes.textSecondary} flex items-center gap-1`}
			>
				<Icon icon={isInDynamicMode ? faCode : faDatabase} />
				<span>
					{isInDynamicMode
						? t('Value will be resolved from execution data')
						: t('Fixed value will be used')}
				</span>
			</div>
		</div>
	);
}
